import React, { useState } from "react";
import { Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";
import { SidebarMenuCardProps } from "./types";
import {
  CardContainer,
  CardTopRow,
  CardIconWrapper,
  ActionRow,
  ActionButton,
  ChildrenContainer,
  ChildItem,
  CardDivider,
} from "./style";

const SidebarMenuCardCst: React.FC<SidebarMenuCardProps> = ({
  icon,
  title,
  description,
  to,
  active = false,
  disabled = false,
  actionButton,
  onClick,
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = children && children.length > 0;

  const handleClick = () => {
    if (disabled) return;
    if (hasChildren) {
      setExpanded((prev) => !prev);
      return;
    }
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    }
  };

  const handleChildClick = (
    e: React.MouseEvent,
    child: { to?: string; onClick?: () => void },
  ) => {
    e.stopPropagation();
    if (child.onClick) {
      child.onClick();
    } else if (child.to) {
      navigate(child.to);
    }
  };

  return (
    <CardContainer
      active={active ? "true" : "false"}
      className={disabled ? "disabled" : ""}
      onClick={handleClick}
    >
      <CardTopRow>
        {icon && <CardIconWrapper>{icon}</CardIconWrapper>}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "13px",
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {title}
        </Typography>
      </CardTopRow>
      {description && (
        <Typography
          sx={{
            fontSize: "11px",
            lineHeight: 1.4,
            color: theme.palette.text.description,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            paddingLeft: "30px",
          }}
        >
          {description}
        </Typography>
      )}
      {actionButton && (
        <ActionRow className="sidebar-card-action-row">
          <ActionButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              actionButton.onClick();
            }}
          >
            <Pencil size={12} />
            {actionButton.label}
          </ActionButton>
        </ActionRow>
      )}
      {hasChildren && (
        <ChildrenContainer
          sx={{
            maxHeight: expanded ? `${children.length * 32 + 24}px` : "0px",
            opacity: expanded ? 1 : 0,
          }}
        >
          <CardDivider />
          {children.map((child, index) => (
            <ChildItem key={index} onClick={(e) => handleChildClick(e, child)}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.primary,
                  lineHeight: 1.4,
                }}
              >
                {child.title}
              </Typography>
            </ChildItem>
          ))}
        </ChildrenContainer>
      )}
    </CardContainer>
  );
};

export default SidebarMenuCardCst;
